import React, { useEffect, useState, useMemo, useRef } from 'react';
import { FullscreenPoiModal, MapSelectionModal } from '@cubemeet/ui';
import { useGlobalToast } from '../../../../../apps/web/src/contexts/ToastContext';
// Prefill placeholder meta based on type to mirror bottom-add behavior
import { createEmptyMealPayload } from '../utils/emptyPayloads/meal';
import { createEmptyPoiPayload } from '../utils/emptyPayloads/poi';
import { createEmptyLodgingPayload } from '../utils/emptyPayloads/lodging';
import { createEmptyActivityPayload } from '../utils/emptyPayloads/activity';
import { createEmptyShoppingPayload } from '../utils/emptyPayloads/shopping';

type PoiModalWrapperProps = {
  openPoi: {
    visible: boolean;
    payload?: any;
    dayIndex?: number;
    itemIndex?: number;
    forceEdit?: boolean;
    fromPlaceholder?: boolean;
  };
  setOpenPoi: (state: { visible: boolean; payload?: any; dayIndex?: number; itemIndex?: number; forceEdit?: boolean; fromPlaceholder?: boolean }) => void;
  editor: any;
  isNarrow: boolean;
  supabaseClient: any;
  currentUserId?: string;
  allowTeamEdit?: boolean;
  isCollaborator?: boolean;
  groupIdForLocal?: string;
  ownerId?: string;
  setDirty: (dirty: boolean) => void;
  dayIndex?: number;
  itemIndex?: number;
  creator?: any;
};

const PoiModalWrapper: React.FC<PoiModalWrapperProps> = ({
  openPoi,
  setOpenPoi,
  editor,
  isNarrow,
  supabaseClient,
  currentUserId,
  allowTeamEdit,
  isCollaborator,
  groupIdForLocal,
  ownerId,
  setDirty,
  dayIndex,
  itemIndex,
  creator,
}) => {
  const [mapModalVisible, setMapModalVisible] = useState<boolean>(false);
  const [mapModalAddress, setMapModalAddress] = useState<string | undefined>(undefined);
  const [mapModalCoords, setMapModalCoords] = useState<{ lat: number; lng: number } | null>(null);
  
  const { showError } = useGlobalToast();
  
  // 使用 ref 保存 dayIndex 和 itemIndex，避免在 modal 關閉時丟失
  const savedDayIndexRef = useRef<number | undefined>(undefined);
  const savedItemIndexRef = useRef<number | undefined>(undefined);
  
  // 當 modal 打開時保存位置信息（只在有有效值時更新）
  useEffect(() => {
    if (openPoi.visible) {
      // 嘗試從多個來源獲取 dayIndex
      const newDayIndex = typeof openPoi.dayIndex === 'number' ? openPoi.dayIndex : 
                          (typeof dayIndex === 'number' ? dayIndex : undefined);
      // 嘗試從多個來源獲取 itemIndex
      const newItemIndex = typeof openPoi.itemIndex === 'number' ? openPoi.itemIndex : 
                           (typeof itemIndex === 'number' ? itemIndex : undefined);
      
      // 只在有有效值時更新 refs（不要用 undefined 覆蓋已有的值）
      if (typeof newDayIndex === 'number') {
        savedDayIndexRef.current = newDayIndex;
      }
      if (typeof newItemIndex === 'number') {
        savedItemIndexRef.current = newItemIndex;
      }
      
      console.log('[PoiModalWrapper] Saved position refs:', { 
        dayIndex: savedDayIndexRef.current, 
        itemIndex: savedItemIndexRef.current,
        'openPoi.dayIndex': openPoi.dayIndex,
        'openPoi.itemIndex': openPoi.itemIndex,
        'prop dayIndex': dayIndex,
        'prop itemIndex': itemIndex,
      });
    }
  }, [openPoi.visible, openPoi.dayIndex, openPoi.itemIndex, dayIndex, itemIndex]);

  useEffect(() => {
    if (!openPoi.visible) {
      setMapModalVisible(false);
      setMapModalAddress(undefined);
      setMapModalCoords(null);
    }
  }, [openPoi.visible]);
  const isOwner = Boolean(currentUserId && ownerId && String(currentUserId) === String(ownerId));
  const canEdit = isOwner || (allowTeamEdit && isCollaborator);

  const forceEditFlag = Boolean(openPoi?.forceEdit || openPoi?.fromPlaceholder || (openPoi?.payload && openPoi.payload._forceEdit));

  // 判断是否是新建卡片（占位卡片首次创建）
  const isCreatingNewCard = useMemo(() => {
    // 優先使用 ref 保存的位置
    const saveDayIndex = savedDayIndexRef.current ?? (typeof openPoi.dayIndex === 'number' ? openPoi.dayIndex : dayIndex);
    const saveItemIndex = savedItemIndexRef.current ?? (typeof openPoi.itemIndex === 'number' ? openPoi.itemIndex : itemIndex);
    
    if (typeof saveDayIndex !== 'number' || typeof saveItemIndex !== 'number') {
      return false;
    }

    const item = editor.itinerary?.days?.[saveDayIndex]?.items?.[saveItemIndex];
    if (!item) return false;

    // 如果是占位卡片（isPlaceholder=true）并且没有 card_id，说明是首次创建
    const isPlaceholder = item.meta?.isPlaceholder === true;
    const hasCardId = !!item.card_id;
    
    return isPlaceholder && !hasCardId;
  }, [openPoi.dayIndex, openPoi.itemIndex, dayIndex, itemIndex, editor.itinerary]);

  // 当 FullscreenPoiModal 保存新卡片后调用（通过 onLoadedCanonical）
  const handleCardCreated = async (card: any) => {
    console.log('[PoiModalWrapper] 新卡片已保存到 cards_cards:', card);
    
    // 優先使用 ref 保存的位置
    const saveDayIndex = savedDayIndexRef.current ?? (typeof openPoi.dayIndex === 'number' ? openPoi.dayIndex : dayIndex);
    const saveItemIndex = savedItemIndexRef.current ?? (typeof openPoi.itemIndex === 'number' ? openPoi.itemIndex : itemIndex);
    
    if (typeof saveDayIndex !== 'number' || typeof saveItemIndex !== 'number') {
      console.error('[PoiModalWrapper] 无法确定行程项位置');
      return;
    }

    const originalItem = editor.itinerary?.days?.[saveDayIndex]?.items?.[saveItemIndex];
    if (!originalItem) {
      console.error('[PoiModalWrapper] 找不到原始行程项');
      return;
    }

    try {
      // 获取当前用户ID
      let currentUserId: string | null = null;
      try {
        const userRes = await supabaseClient.auth.getUser();
        currentUserId = userRes?.data?.user?.id || null;
      } catch (e) {
        console.warn('[PoiModalWrapper] 无法获取当前用户ID', e);
      }

      // 获取group_id和day信息
      const groupId = groupIdForLocal;
      if (!groupId) {
        throw new Error('缺少group_id，无法保存');
      }

      const dayNumber = (saveDayIndex || 0) + 1;
      const dayDate = editor.itinerary?.days?.[saveDayIndex]?.date || null;
      const sortOrder = saveItemIndex;

      // 检查是否已经有 itinerary entry（占位卡片可能已经创建了entry）
      let entryId = originalItem.id;
      const isTempId = !entryId || /^\d+_[a-z0-9]+$/i.test(String(entryId));

      if (isTempId) {
        // 创建新的 itinerary entry，关联到新卡片
        const row: any = {
          group_id: groupId,
          day_number: dayNumber,
          day_date: dayDate,
          sort_order: sortOrder,
          item_type: 'card',  // 关联卡片类型
          card_id: card.id,   // 引用 cards_cards
          note: originalItem.note || null,
          start_time: originalItem.start_time || null,
          end_time: originalItem.end_time || null,
          creator_id: currentUserId,
          // 不需要 custom_payload，因为数据在 cards_cards 中
        };

        console.log('🔍 [PoiModalWrapper] 准备INSERT entry:', row);

        const { data: inserted, error: insertErr } = await supabaseClient
          .from('group_itinerary_entries')
          .insert([row])
          .select()
          .maybeSingle();

        if (insertErr) {
          console.error('[PoiModalWrapper] INSERT entry 失败:', insertErr);
          throw insertErr;
        }

        entryId = inserted?.id;
        console.log('🔍 [PoiModalWrapper] 新entry ID:', entryId);
      } else {
        // 更新现有 entry，关联到新卡片
        const { error: updateErr } = await supabaseClient
          .from('group_itinerary_entries')
          .update({
            card_id: card.id,
            item_type: 'card',
            custom_payload: null,  // 清空副本，使用 cards_cards 数据
            inline_payload: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', entryId);

        if (updateErr) {
          console.error('[PoiModalWrapper] UPDATE entry 失败:', updateErr);
          throw updateErr;
        }

        console.log('🔍 [PoiModalWrapper] 已更新entry关联到卡片:', card.id);
      }

      try {
        // 更新前端状态
        const frontendPatch = {
          ...originalItem,
          id: entryId,
          card_id: card.id,
          card: card,  // 添加完整的卡片对象
          item_type: 'card',
          custom_payload: null,
          inline_payload: null,
          payload: card.base_payload || {},
          title: card.title,
          description: card.description,
          address: card.address,  // ✅ 添加顶级 address
          cover_image: card.cover_image,  // ✅ 添加顶级封面
          meta: {
            ...(originalItem.meta || {}),
            itineraryEntryId: entryId,
            isPlaceholder: false,  // 移除占位标志
          },
        };

        editor.updateItem(saveDayIndex, saveItemIndex, frontendPatch);
        setDirty(true);

        console.log('[PoiModalWrapper] 行程项已更新:', frontendPatch);
        
        // 关闭modal
        setOpenPoi({ visible: false, payload: undefined, dayIndex: undefined, itemIndex: undefined, forceEdit: undefined, fromPlaceholder: undefined });
      } catch (frontendError) {
        console.error('[PoiModalWrapper] 前端状态更新失败 (DB已更新):', frontendError);
        // 不抛出错误，因为DB已经更新成功
        try {
          setOpenPoi({ visible: false, payload: undefined, dayIndex: undefined, itemIndex: undefined, forceEdit: undefined, fromPlaceholder: undefined });
        } catch (e) {}
      }
    } catch (error) {
      console.error('[PoiModalWrapper] 关联卡片失败:', error);
      showError && showError('保存失败，请重试');
    }
  };

  // 编辑现有卡片的副本（不影响 cards_cards）
  const handleSave = async (updatedPayload: any) => {
    console.log('[PoiModalWrapper] handleSave - 编辑副本');
    console.log('[PoiModalWrapper] updatedPayload:', updatedPayload);
    console.log('[PoiModalWrapper] updatedPayload.description:', updatedPayload?.description);
    console.log('[PoiModalWrapper] openPoi:', openPoi);
    console.log('[PoiModalWrapper] dayIndex prop:', dayIndex);
    console.log('[PoiModalWrapper] itemIndex prop:', itemIndex);
    console.log('[PoiModalWrapper] savedDayIndexRef:', savedDayIndexRef.current, 'savedItemIndexRef:', savedItemIndexRef.current);
    
    // 優先使用 ref 保存的位置，確保不會丟失
    const saveDayIndex = savedDayIndexRef.current ?? (typeof openPoi.dayIndex === 'number' ? openPoi.dayIndex : dayIndex);
    const saveItemIndex = savedItemIndexRef.current ?? (typeof openPoi.itemIndex === 'number' ? openPoi.itemIndex : itemIndex);
    
    console.log('[PoiModalWrapper] saveDayIndex:', saveDayIndex, 'saveItemIndex:', saveItemIndex);
    
    if (typeof saveDayIndex !== 'number' || typeof saveItemIndex !== 'number') {
      const errorMsg = "保存失败：项目位置未知。";
      console.error('[PoiModalWrapper] ' + errorMsg, { openPoi, dayIndex, itemIndex, saveDayIndex, saveItemIndex });
      showError && showError(errorMsg);
      return Promise.reject(new Error(errorMsg));
    }

    const originalItem = editor.itinerary?.days?.[saveDayIndex]?.items?.[saveItemIndex];
    
    // 添加詳細調試信息
    console.log('[PoiModalWrapper] editor.itinerary:', editor.itinerary);
    console.log('[PoiModalWrapper] days count:', editor.itinerary?.days?.length);
    console.log('[PoiModalWrapper] day at saveDayIndex:', editor.itinerary?.days?.[saveDayIndex]);
    console.log('[PoiModalWrapper] items in day:', editor.itinerary?.days?.[saveDayIndex]?.items);
    console.log('[PoiModalWrapper] originalItem:', originalItem);
    
    if (!originalItem || !originalItem.id) {
      const errorMsg = "保存失败：找不到原始项目。";
      console.error(`[PoiModalWrapper] ${errorMsg}`, {
        saveDayIndex,
        saveItemIndex,
        daysCount: editor.itinerary?.days?.length,
        itemsCount: editor.itinerary?.days?.[saveDayIndex]?.items?.length,
        originalItem,
      });
      showError && showError(errorMsg);
      return Promise.reject(new Error(errorMsg));
    }

    try {
      // 更新 custom_payload 和 note 字段（描述）
      const dbUpdatePayload: any = {
        custom_payload: updatedPayload,
        inline_payload: updatedPayload,
        updated_at: new Date().toISOString(),
      };
      
      // 如果有 description，同步更新 note 字段
      if (updatedPayload.description !== undefined) {
        dbUpdatePayload.note = updatedPayload.description;
      }

      console.log('🔍 [PoiModalWrapper] 更新副本, dbUpdatePayload:', dbUpdatePayload);
      console.log('🔍 [PoiModalWrapper] entry id:', originalItem.id);

      const { error } = await supabaseClient
        .from('group_itinerary_entries')
        .update(dbUpdatePayload)
        .eq('id', originalItem.id);

      if (error) {
        console.error('[PoiModalWrapper] DB update error:', error);
        throw error;
      }

      console.log('[PoiModalWrapper] DB update success');

      try {
        // 更新前端状态
        const frontendPatch = {
          ...originalItem,
          custom_payload: updatedPayload,
          inline_payload: updatedPayload,
          payload: updatedPayload,
          updated_at: new Date().toISOString(),
          title: updatedPayload.title || originalItem.title,
          // 同步更新 description 和 note 字段
          description: updatedPayload.description ?? originalItem.description,
          note: updatedPayload.description ?? originalItem.note,
        };

        console.log('[PoiModalWrapper] frontendPatch:', frontendPatch);

        editor.updateItem(saveDayIndex, saveItemIndex, frontendPatch);
        setDirty(true);
        
        console.log('[PoiModalWrapper] 副本已更新');
        
        // 关闭modal
        setOpenPoi({ visible: false, payload: undefined, dayIndex: undefined, itemIndex: undefined, forceEdit: undefined, fromPlaceholder: undefined });
      } catch (frontendError) {
        console.error('[PoiModalWrapper] 前端状态更新失败 (DB已更新):', frontendError);
        // 不抛出错误，因为DB已经更新成功，避免给用户显示"保存失败"的误导
        // 关闭modal以刷新状态（如果可能）
        try {
          setOpenPoi({ visible: false, payload: undefined, dayIndex: undefined, itemIndex: undefined, forceEdit: undefined, fromPlaceholder: undefined });
        } catch (e) {}
      }
    } catch (error) {
      console.error('[PoiModalWrapper] 更新副本失败:', error);
      showError && showError('保存失败，请重试');
      throw error;
    }
  };

  // 以下是现有的获取数据逻辑...

  // CRITICAL: when opening from itinerary, openPoi.payload may be a snapshot or contain the original item under `_item`.
  // Prefer the original full item if present, so we can access custom_payload.metaPairs directly.
  const itineraryItem = (openPoi as any)?.payload?._item || openPoi.payload;
  const authoritativePayload = itineraryItem?.custom_payload || itineraryItem?.inline_payload || itineraryItem?.payload || openPoi.payload || {};
  // metaPairs 優先從 custom_payload 讀取（行程副本），其次從 card.base_payload 讀取（原始卡片）
  const authoritativeMeta = authoritativePayload?.metaPairs 
    || itineraryItem?.card?.base_payload?.metaPairs 
    || itineraryItem?.card?.metaPairs
    || itineraryItem?.meta 
    || [];
  
  // Debug log
  if (openPoi.visible) {
    console.log('[PoiModalWrapper] metaPairs sources:', {
      'authoritativePayload?.metaPairs': authoritativePayload?.metaPairs,
      'itineraryItem?.card?.base_payload?.metaPairs': itineraryItem?.card?.base_payload?.metaPairs,
      'itineraryItem?.card?.metaPairs': itineraryItem?.card?.metaPairs,
      'itineraryItem?.meta': itineraryItem?.meta,
      'authoritativeMeta': authoritativeMeta,
      'itineraryItem': itineraryItem,
    });
  }
  
  const isPlaceholder = Boolean(itineraryItem?.meta?.isPlaceholder);
  const itemType = String(itineraryItem?.type || '').toLowerCase() || 'poi';

  const hasLinkedCard = (() => {
    try {
      const cardRef = itineraryItem?.card;
      if (!cardRef) return false;
      if (Array.isArray(cardRef)) return cardRef.length > 0;
      if (typeof cardRef === 'object') {
        const keys = Object.keys(cardRef);
        if (keys.length === 0) return false;
        const meaningful = ['id', 'cardId', 'card_id', 'base_payload', 'payload', 'title', 'name'];
        return meaningful.some((k) => {
          const val = (cardRef as any)[k];
          if (val === null || typeof val === 'undefined') return false;
          if (typeof val === 'string') return val.trim().length > 0;
          if (typeof val === 'object') return Object.keys(val).length > 0;
          return true;
        });
      }
      return true;
    } catch (e) {
      return false;
    }
  })();

  const shouldStartInEditMode = forceEditFlag || (isPlaceholder && !hasLinkedCard);

  useEffect(() => {
    if (!openPoi?.visible) return;
    console.log('[PoiModalWrapper DEBUG] edit-mode flags', {
      forceEditFlag,
      fromPlaceholder: openPoi?.fromPlaceholder,
      payloadForce: openPoi?.payload?._forceEdit,
      isPlaceholder,
      hasLinkedCard,
      shouldStartInEditMode,
    });
  }, [openPoi?.visible, forceEditFlag, openPoi?.fromPlaceholder, openPoi?.payload, isPlaceholder, hasLinkedCard, shouldStartInEditMode]);

  // Generate placeholder metaPairs for empty placeholders (mirror QuickAdd -> bottom flow)
  // DISABLED: Let users add fields via bottom buttons instead of auto-injecting
  const generatedPlaceholderMeta: Array<{ label: string; value: string }> = useMemo(() => {
    return [];
    /*
    if (!isPlaceholder) return [];
    const hasMeta = Array.isArray(authoritativeMeta) && authoritativeMeta.length > 0;
    if (hasMeta) return [];
    try {
      if (itemType === 'restaurant' || itemType === 'meal' || itemType === 'food') {
        return (createEmptyMealPayload()?.metaPairs || []).map((p: any) => ({ label: p.label, value: '' }));
      }
      if (itemType === 'lodging' || itemType === 'hotel') {
        return (createEmptyLodgingPayload()?.metaPairs || []).map((p: any) => ({ label: p.label, value: '' }));
      }
      if (itemType === 'shopping' || itemType === 'shop' || itemType === 'mall') {
        return (createEmptyShoppingPayload()?.metaPairs || []).map((p: any) => ({ label: p.label, value: '' }));
      }
      if (itemType === 'activity' || itemType === 'event') {
        return (createEmptyActivityPayload()?.metaPairs || []).map((p: any) => ({ label: p.label, value: '' }));
      }
      // default poi
      return (createEmptyPoiPayload()?.metaPairs || []).map((p: any) => ({ label: p.label, value: '' }));
    } catch {
      return [];
    }
    */
  }, [isPlaceholder, authoritativeMeta, itemType]);
  
  // Opening modal with authoritative data (debug logging removed)

  // If snapshot lacks an address but we can derive a canonical card id, fetch the
  // canonical `cards_cards` row as a fallback so the modal can show an address
  // immediately for display purposes. This does NOT change edit/save semantics.
  const [fetchedCanonical, setFetchedCanonical] = useState<any>(null);
  useEffect(() => {
    let mounted = true;
    
    // CRITICAL: Clear previous card data when modal closes or item changes to prevent state pollution
    if (!openPoi.visible) {
      setFetchedCanonical(null);
      return;
    }
    
    // Also clear when switching to a different item (especially important for placeholders)
    setFetchedCanonical(null);
    
    (async () => {
      try {
        // If itineraryItem or authoritativePayload already has an address, nothing to do
        const hasAddress = Boolean(itineraryItem?.address || authoritativePayload?.address);
        if (hasAddress) return;
        if (!supabaseClient) return;

        // Derive candidate card id from common locations
        const candidateId = (itineraryItem && (itineraryItem.meta?.cardId || itineraryItem.card_id || itineraryItem.cardId || itineraryItem.id))
          || (authoritativePayload && (authoritativePayload.cardId || authoritativePayload.card_id || authoritativePayload.id));
        if (!candidateId) return;

        const { data: full, error } = await supabaseClient.from('cards_cards').select('*').eq('id', candidateId).maybeSingle();
        if (!mounted) return;
        if (!error && full) setFetchedCanonical(full);
      } catch (e) {
        // ignore fetch errors, this is only a best-effort display enhancement
        try { console.warn('[PoiModalWrapper] fetch canonical card failed', e); } catch (err) {}
      }
    })();
    return () => { mounted = false; };
  }, [openPoi.visible, itineraryItem, authoritativePayload, supabaseClient]);

  // If the caller (e.g. list page) provided a creator/profile object, and the itinerary
  // snapshot does not already include embedded creator info, attach it to the item we pass
  // down to the modal. This allows FullscreenPoiModal to detect inline creator info and
  // avoid an extra profiles fetch which causes ShareButton to appear late.
  let cardToPass = itineraryItem;
  try {
    // Check if item has inline creator info
    const hasInline = !!(itineraryItem && (itineraryItem.creator || itineraryItem.creator_profile || itineraryItem.creatorProfile || itineraryItem.owner || itineraryItem.owner_profile));
    
    if (!hasInline) {
      // Check if item has creator_id (from group_itinerary_entries)
      const itemCreatorId = itineraryItem?.creator_id || itineraryItem?.creatorId;
      
      // If creator prop matches the item's creator_id, use it
      if (creator && creator.id && itemCreatorId && String(creator.id) === String(itemCreatorId)) {
        cardToPass = Object.assign({}, itineraryItem || {});
        cardToPass.creator_profile = creator;
        cardToPass.creator = creator;
      }
      // If item has creator_id but no profile, and it matches current user, use current user info
      else if (itemCreatorId && currentUserId && String(itemCreatorId) === String(currentUserId)) {
        // Try to get current user's profile from memberProfiles
        cardToPass = Object.assign({}, itineraryItem || {});
        cardToPass.creator_id = itemCreatorId;
        
        // Attach profile if available in memberProfiles
        if (memberProfiles) {
          const profileMap = memberProfiles instanceof Map ? memberProfiles : (memberProfiles as Record<string, any>);
          const currentUserProfile = profileMap instanceof Map 
            ? profileMap.get(String(currentUserId))
            : profileMap[String(currentUserId)];
          
          if (currentUserProfile) {
            cardToPass.creator_profile = currentUserProfile;
            cardToPass.creator = currentUserProfile;
          }
        }
      }
      // Fallback: use page-level creator if provided
      else if (creator && creator.id) {
        cardToPass = Object.assign({}, itineraryItem || {});
        cardToPass.creator_profile = creator;
        cardToPass.creator = creator;
      }
    }
  } catch (e) {
    // ignore
  }

  // If we fetched canonical card data as a fallback, merge non-destructively so
  // FullscreenPoiModal can immediately display canonical fields (like address).
  // We avoid mutating editor state by creating a shallow copy.
  if (fetchedCanonical) {
    try {
      cardToPass = Object.assign({}, cardToPass || {}, fetchedCanonical || {});
    } catch (e) {}
  }

  // Placeholder entries should not expose their itinerary id as a card id; otherwise the modal will
  // treat them as real cards and trigger a Supabase lookup, which blocks the edit UI. Strip IDs so
  // the modal recognises them as new/unsaved cards and skips the canonical fetch.
  if (isPlaceholder) {
    try {
      cardToPass = Object.assign({}, cardToPass || {});
      delete (cardToPass as any).id;
      delete (cardToPass as any).cardId;
      delete (cardToPass as any).card_id;
      if (cardToPass && typeof cardToPass === 'object' && 'payload' in (cardToPass as any)) {
        const payloadClone = Object.assign({}, (cardToPass as any).payload || {});
        delete payloadClone.id;
        delete payloadClone.cardId;
        delete payloadClone.card_id;
        (cardToPass as any).payload = payloadClone;
      }
      if (cardToPass && typeof cardToPass === 'object' && 'custom_payload' in (cardToPass as any)) {
        const customClone = Object.assign({}, (cardToPass as any).custom_payload || {});
        delete customClone.id;
        delete customClone.cardId;
        delete customClone.card_id;
        (cardToPass as any).custom_payload = customClone;
      }
    } catch (e) {}
  }

  // Prefetch avatar image if available to warm browser cache and avoid modal-time requests/flash
  try {
    const avatarUrl = (cardToPass && (cardToPass.creator_profile && cardToPass.creator_profile.avatar_url)) || (creator && creator.avatar_url) || null;
    if (typeof window !== 'undefined' && avatarUrl) {
      try {
        const img = new Image();
        img.src = avatarUrl;
      } catch (e) {
        // ignore prefetch failures
      }
    }
  } catch (e) {}

  return (
    <>
      <FullscreenPoiModal
        visible={openPoi.visible}
  onClose={() => setOpenPoi({ visible: false, payload: undefined, dayIndex: undefined, itemIndex: undefined, forceEdit: undefined, fromPlaceholder: undefined })}
  // Pass the COMPLETE itinerary item (including custom_payload) so modal can read custom_payload.metaPairs
        // pass the possibly-augmented item (with creator_profile) to avoid refetching
        card={cardToPass}
        // If opening from an empty placeholder (no metaPairs/OSM info), start in edit mode so
        // usePoiEditing can generate the preset placeholder meta fields for the chosen type.
        initialEditMode={shouldStartInEditMode}
        // 傳入 authoritativeMeta（來自 custom_payload.metaPairs）
        metaData={authoritativeMeta}
        // For placeholders, pass empty title to allow modal to show dynamic placeholder text
        title={isPlaceholder ? '' : (itineraryItem?.title || authoritativePayload?.title || '')}
        // description 優先從 itineraryItem 讀取（會同步更新 note 字段）
        description={itineraryItem?.description || authoritativePayload?.description}
        address={(() => {
          // 优先级：itineraryItem.address > card.address > authoritativePayload.address > fetchedCanonical.address
          const fromItem = itineraryItem?.address;
          const fromCard = itineraryItem?.card?.address || itineraryItem?.card?.base_payload?.address;
          const fromPayload = authoritativePayload?.address;
          const fromCanonical = ((!isPlaceholder || itineraryItem?.card_id) && fetchedCanonical) 
            ? (fetchedCanonical.address || fetchedCanonical.base_payload?.address)
            : null;
          
          const resolved = fromItem || fromCard || fromPayload || fromCanonical;
          
          if (typeof window !== 'undefined' && window.location.host.includes('localhost')) {
            console.log('[PoiModalWrapper address debug]', {
              resolved,
              fromItem,
              fromCard,
              fromPayload,
              fromCanonical,
              'itineraryItem.card_id': itineraryItem?.card_id,
              'isPlaceholder': isPlaceholder,
            });
          }
          
          return resolved || undefined;
        })()}
        imageUrl={(() => {
          // Debug: log all possible image sources
          if (typeof window !== 'undefined' && window.location.host.includes('localhost')) {
            console.log('[PoiModalWrapper imageUrl debug]', {
              'itineraryItem.card?.cover_image': itineraryItem?.card?.cover_image,
              'authoritativePayload?.cover_image': authoritativePayload?.cover_image,
              'fetchedCanonical?.cover_image': fetchedCanonical?.cover_image,
              'isPlaceholder': isPlaceholder,
              'hasLinkedCard': hasLinkedCard,
              'itineraryItem.card_id': itineraryItem?.card_id,
            });
          }
          
          // 对于新的占位卡片（没有 card_id），不使用 fetchedCanonical
          const shouldUseFetchedCanonical = !isPlaceholder || (isPlaceholder && itineraryItem?.card_id);
          
          // Prefer any real image field we can find
          const fromAuthoritative =
            authoritativePayload?.cover_image
            || authoritativePayload?.coverImage
            || authoritativePayload?.image_url
            || authoritativePayload?.image
            || null;
          const fromCard = (itineraryItem && itineraryItem.card)
            ? (
              itineraryItem.card.cover_image
              || itineraryItem.card.coverImage
              || (itineraryItem.card.base_payload && (itineraryItem.card.base_payload.cover_image || (itineraryItem.card.base_payload as any).coverImage))
              || itineraryItem.card.image
              || itineraryItem.card.image_url
            )
            : null;
          const fromCanonical = (shouldUseFetchedCanonical && fetchedCanonical)
            ? (
              fetchedCanonical.cover_image
              || fetchedCanonical.coverImage
              || (fetchedCanonical.base_payload && (fetchedCanonical.base_payload.cover_image || (fetchedCanonical.base_payload as any).coverImage))
              || fetchedCanonical.image
              || fetchedCanonical.image_url
            )
            : null;

          const resolved = fromAuthoritative || fromCard || fromCanonical || null;
          
          if (typeof window !== 'undefined' && window.location.host.includes('localhost')) {
            console.log('[PoiModalWrapper imageUrl resolved]', { resolved, fromAuthoritative, fromCard, fromCanonical });
          }

          // 如果没有图片，返回 undefined（不渲染图片区域）
          // 只有在编辑模式的占位卡片才返回空字符串（显示上传按钮）
          if (!resolved) {
            // 占位卡片且在编辑模式：返回空字符串显示上传区域
            if (isPlaceholder && shouldStartInEditMode) return '';
            // 否则返回 undefined（不渲染图片区域）
            return undefined;
          }
          
          return resolved;
        })()}
        tags={authoritativePayload?.tags || []}
        canEdit={canEdit}
        isOwner={isOwner}
        supabaseClient={supabaseClient}
        currentUserId={currentUserId}
        // 关键逻辑：根据是否是新建卡片决定回调
        // 新建卡片：不提供 onSave，让 FullscreenPoiModal 内部保存到 cards_cards
        //         提供 onLoadedCanonical 来处理新卡片并创建 itinerary entry
        // 编辑卡片：提供 onSave，只更新 custom_payload，不影响 cards_cards
        onSave={isCreatingNewCard ? undefined : handleSave}
        onLoadedCanonical={isCreatingNewCard ? handleCardCreated : undefined}
        onRequestMapNavigation={({ address: reqAddress, coords }) => {
          const strAddress = typeof reqAddress === 'string' && reqAddress.trim().length > 0
            ? reqAddress.trim()
            : undefined;
          const fallbackAddress = strAddress
            || (typeof (itineraryItem?.address) === 'string' ? itineraryItem?.address : undefined)
            || (typeof (authoritativePayload?.address) === 'string' ? authoritativePayload?.address : undefined)
            || (typeof (fetchedCanonical?.address) === 'string' ? fetchedCanonical?.address : undefined);

          const validCoords = (coords && typeof coords.lat === 'number' && typeof coords.lng === 'number')
            ? { lat: coords.lat, lng: coords.lng }
            : null;

          if (!fallbackAddress && !validCoords) {
            return;
          }

          setMapModalAddress(fallbackAddress);
          setMapModalCoords(validCoords);
          setMapModalVisible(true);
        }}
      />
      <MapSelectionModal
        visible={mapModalVisible}
        onClose={() => setMapModalVisible(false)}
        address={mapModalAddress}
        coords={mapModalCoords}
      />
    </>
  );
};

export default PoiModalWrapper;
